import React, { useEffect, useState } from "react";
import "./Profile.css";
import { ArrowLeft, Check, Loader2 } from "lucide-react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { supabase } from "../supabaseClient";

export default function Profile() {
  const navigate = useNavigate();
  const { userAvatar, setUserAvatar } = useOutletContext();

  const [email, setEmail] = useState("");
  const [kycStatus, setKycStatus] = useState("unverified");
  const [username, setUsername] = useState("");
  const [traderId, setTraderId] = useState("");

  const [lastChange, setLastChange] = useState(null);
  const [saving, setSaving] = useState(false);
  const [canSave, setCanSave] = useState(true);

  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Delete account modal states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [otpRequired, setOtpRequired] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Load from localStorage (fast)
  useEffect(() => {
    const cache = localStorage.getItem("profile_cache");
    if (cache) {
      const p = JSON.parse(cache);
      if (p.username) setUsername(p.username);
      if (p.kyc_status) setKycStatus(p.kyc_status);
      if (p.avatar_url) setUserAvatar(p.avatar_url);
      if (p.email) setEmail(p.email);
      if (p.trader_id) setTraderId(p.trader_id);
    }
  }, []);

  // Load from Supabase (slow but accurate)
  useEffect(() => {
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth?.user) return;
      const uid = auth.user.id;

      if (auth.user.email) setEmail(auth.user.email);

      const { data } = await supabase
        .from("users_data")
        .select("username, kyc_status, username_last_change, avatar_url, trader_id")
        .eq("user_id", uid)
        .single();

      if (!data) return;

      if (data.username) setUsername(data.username);
      if (data.kyc_status) setKycStatus(data.kyc_status);
      if (data.avatar_url) setUserAvatar(data.avatar_url);

      if (data.username_last_change) {
        setLastChange(data.username_last_change);
        const diff =
          (Date.now() - new Date(data.username_last_change)) /
          (1000 * 60 * 60 * 24);
        if (diff < 7) setCanSave(false);
      }

      // Generate Trader ID if missing
      if (!data.trader_id) {
        const newId =
          "TRD-" + Math.floor(1000000000 + Math.random() * 9000000000);

        await supabase
          .from("users_data")
          .update({
            trader_id: newId,
            updated_at: new Date().toISOString(),
          })
          .eq("user_id", uid);

        setTraderId(newId);
      } else {
        setTraderId(data.trader_id);
      }

      // Update local cache
      localStorage.setItem(
        "profile_cache",
        JSON.stringify({
          username: data.username,
          kyc_status: data.kyc_status,
          avatar_url: data.avatar_url,
          email: auth.user.email,
          trader_id: data.trader_id || traderId,
        })
      );
    })();
  }, []);
// Listen for realtime KYC updates
useEffect(() => {
  (async () => {
    const { data: auth } = await supabase.auth.getUser();
    if (!auth?.user) return;

    const uid = auth.user.id;

    const channel = supabase
      .channel(`kyc-updates-${uid}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "users_data",
          filter: `user_id=eq.${uid}`,
        },
        (payload) => {
          const newStatus = payload.new.kyc_status;
          setKycStatus(newStatus);

          // Update localStorage also
          let cache = JSON.parse(localStorage.getItem("profile_cache") || "{}");
          cache.kyc_status = newStatus;
          localStorage.setItem("profile_cache", JSON.stringify(cache));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  })();
}, []);

  // Avatar Upload
  const uploadAvatarToSupabase = async (file, uid) => {
    const clean = file.name.replace(/[^a-zA-Z0-9.\-_]/g, "_");
    const path = `avatars/${uid}/${Date.now()}-${clean}`;

    const { error: upErr } = await supabase.storage
      .from("avatars")
      .upload(path, file, { upsert: true });

    if (upErr) throw upErr;

    const { data: url } = supabase.storage.from("avatars").getPublicUrl(path);
    return url.publicUrl;
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingAvatar(true);

    const { data: auth } = await supabase.auth.getUser();
    const uid = auth?.user?.id;

    try {
      const publicUrl = await uploadAvatarToSupabase(file, uid);

      await supabase
        .from("users_data")
        .update({
          avatar_url: publicUrl,
          updated_at: new Date().toISOString(),
        })
        .eq("user_id", uid);

      setUserAvatar(publicUrl);

      const cache = JSON.parse(localStorage.getItem("profile_cache") || "{}");
      cache.avatar_url = publicUrl;
      localStorage.setItem("profile_cache", JSON.stringify(cache));
    } catch (err) {
      console.error("Avatar upload error:", err);
    }

    setUploadingAvatar(false);
  };

  // Save Username
  const saveUsername = async () => {
    if (!canSave) return;

    setSaving(true);
    const now = new Date().toISOString();

    const { data: auth } = await supabase.auth.getUser();
    const uid = auth?.user?.id;

    await supabase
      .from("users_data")
      .update({
        username,
        username_last_change: now,
        updated_at: now,
      })
      .eq("user_id", uid);

    setSaving(false);
    setCanSave(false);
    setLastChange(now);

    const cache = JSON.parse(localStorage.getItem("profile_cache") || "{}");
    cache.username = username;
    localStorage.setItem("profile_cache", JSON.stringify(cache));
  };

  // Check MFA authenticator enabled?
  const check2FAStatus = async () => {
    const { data } = await supabase.auth.mfa.listFactors();
    return data?.totp?.length > 0;
  };

  // Delete Account (FIXED)
  const deleteAccount = async () => {
    setDeleting(true);

    const { data: auth } = await supabase.auth.getUser();
    const uid = auth?.user?.id;
    if (!uid) return;

    try {
      if (otpRequired) {
        const factors = await supabase.auth.mfa.listFactors();

        const verify = await supabase.auth.mfa.verify({
          factorId: factors.data.totp[0].id,
          code: otpCode,
        });

        if (verify.error) {
          alert("Invalid authenticator code!");
          setDeleting(false);
          return;
        }
      }

      // --------- 🔥 FIX — PROPER SUPABASE EDGE FUNCTION DELETE ----------
      const { error } = await supabase.functions.invoke("delete-user", {
        body: { user_id: uid },
      });

      if (error) {
        alert("Failed to delete account. Try again.");
        setDeleting(false);
        return;
      }
      // ------------------------------------------------------------------

      localStorage.clear();
      navigate("/signup");
    } catch (err) {
      console.error("Delete error:", err);
    }

    setDeleting(false);
  };

  return (
    <div className="profile-page">
      <h2 className="profile-title">My Profile</h2>

      <div className="profile-card">
        <div className="avatar-section">
          <img
            src={userAvatar || "/user.svg"}
            className="profile-avatar"
            alt="User"
            onError={(e) => (e.currentTarget.src = "/user.svg")}
          />

          <label className="upload-btn">
            {uploadingAvatar ? "Uploading…" : "Change Photo"}
            <input type="file" accept="image/*" onChange={handleUpload} />
          </label>

          <div className={`verify-status ${kycStatus}`}>
            {kycStatus === "verified"
              ? "Verified ✔"
              : kycStatus === "pending"
              ? "Pending ⧗"
              : "Unverified ✖"}
          </div>
        </div>

        <div className="profile-info">
          <div className="field">
            <label>Trader ID</label>
            <input value={traderId || ""} disabled />
          </div>

          <div className="field username-field">
            <label>Username</label>
            <div className="username-row">
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />

              <button
                className="save-username-btn"
                disabled={!canSave || saving}
                onClick={saveUsername}
              >
                {saving ? (
                  <Loader2 className="spin" size={16} />
                ) : (
                  <Check size={16} />
                )}
              </button>
            </div>

            {!canSave && (
              <small className="username-note">
                You can change your username again after 7 days.
              </small>
            )}
          </div>

          <div className="field">
            <label>Email</label>
            <input value={email} disabled />
          </div>

          {kycStatus === "unverified" && (
            <button className="kyc-btn" onClick={() => navigate("/kyc")}>
              Submit Verification Documents
            </button>
          )}

          {kycStatus === "pending" && (
            <div className="kyc-pending-box">Verification Pending…</div>
          )}

          {kycStatus === "verified" && (
            <div className="kyc-success-box">KYC Verified ✔</div>
          )}

          <button
            className="delete-btn"
            onClick={async () => {
              const has2FA = await check2FAStatus();
              if (has2FA) setOtpRequired(true);
              setShowDeleteConfirm(true);
            }}
          >
            Delete Account
          </button>
        </div>
      </div>

      <div className="back-btn-wrapper">
        <button className="back-btn" onClick={() => navigate("/")}>
          <ArrowLeft size={20} /> Back
        </button>
      </div>

      {/* DELETE ACCOUNT MODAL */}
      {showDeleteConfirm && (
        <div className="premium-modal-overlay">
          <div className="premium-modal-card">
            <h3 className="modal-title">Delete Account?</h3>

            <p className="modal-text">
              This action is permanent. All profile data, balance, history and
              progress will be permanently removed.
            </p>

            {otpRequired && (
              <div className="otp-box">
                <label>Authenticator Code</label>
                <input
                  type="text"
                  maxLength={6}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  placeholder="Enter 6-digit code"
                />
              </div>
            )}

            <div className="modal-buttons">
              <button
                className="cancel-btn"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>

              <button
                className="confirm-delete-btn"
                onClick={deleteAccount}
                disabled={deleting}
              >
                {deleting ? "Deleting…" : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
